import { Check, X } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";

// Values are language-agnostic — only labels are translated
const VALUES = [
    [true, true, "partial", false],
    [true, false, false, false],
    [true, false, false, false],
    [true, false, false, false],
    [true, false, false, false],
    [true, false, false, false],
    [true, false, true, true],
    [true, false, false, true],
    [true, false, false, false],
];

function Cell({ v, partialLabel }) {
    if (v === true)
        return (
            <span className="inline-flex h-7 w-7 rounded-md bg-[#D4FF00]/15 text-[#D4FF00] items-center justify-center">
                <Check size={16} strokeWidth={3} />
            </span>
        );
    if (v === "partial")
        return (
            <span className="inline-flex px-2.5 py-1 rounded-md bg-white/5 text-white/65 text-[10px] uppercase tracking-wider font-bold">
                {partialLabel}
            </span>
        );
    return (
        <span className="inline-flex h-7 w-7 rounded-md bg-white/[0.04] text-white/30 items-center justify-center">
            <X size={16} />
        </span>
    );
}

export default function Competitive() {
    const { t } = useI18n();
    const c = t.competitive;
    return (
        <section
            id="competitive"
            data-testid="competitive-section"
            className="relative py-24 md:py-32 border-t border-white/5"
        >
            <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                <div className="grid lg:grid-cols-12 gap-8 mb-14 reveal">
                    <div className="lg:col-span-7">
                        <div className="text-xs tracking-[0.22em] uppercase font-bold text-[#D4FF00] mb-4">
                            {c.tag}
                        </div>
                        <h2 className="font-display font-bold uppercase text-4xl md:text-5xl lg:text-6xl tracking-tighter heading-clamp">
                            {c.headline}{" "}
                            <span className="text-[#D4FF00]">{c.headlineAccent}</span>
                        </h2>
                    </div>
                    <div className="lg:col-span-5 self-end">
                        <p className="text-white/65 text-lg leading-relaxed">{c.sub}</p>
                    </div>
                </div>

                <div
                    data-testid="competitive-table"
                    className="reveal overflow-hidden rounded-2xl border border-white/10 bg-[#071B12]"
                >
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead>
                                <tr className="border-b border-white/10">
                                    <th className="px-5 md:px-7 py-5 text-[11px] uppercase tracking-widest text-white/45 font-bold min-w-[260px]">
                                        {c.feature}
                                    </th>
                                    {c.cols.map((col, i) => (
                                        <th
                                            key={col}
                                            className={`px-5 md:px-7 py-5 text-center text-[11px] uppercase tracking-widest font-bold ${
                                                i === 0
                                                    ? "text-[#D4FF00] bg-[#D4FF00]/[0.05]"
                                                    : "text-white/55"
                                            }`}
                                        >
                                            {col}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {c.rows.map((label, ri) => (
                                    <tr
                                        key={ri}
                                        className="border-b border-white/5 last:border-0 hover:bg-white/[0.015]"
                                    >
                                        <td className="px-5 md:px-7 py-4 text-sm md:text-[15px] text-white/85 font-medium">
                                            {label}
                                        </td>
                                        {VALUES[ri].map((v, ci) => (
                                            <td
                                                key={ci}
                                                className={`px-5 md:px-7 py-4 text-center ${
                                                    ci === 0 ? "bg-[#D4FF00]/[0.04]" : ""
                                                }`}
                                            >
                                                <Cell v={v} partialLabel={c.partial} />
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {c.footnote && (
                        <div className="px-5 md:px-7 py-3 border-t border-white/5 text-[11px] text-white/40 leading-relaxed">
                            {c.footnote}
                        </div>
                    )}
                </div>

                <div className="mt-10 reveal">
                    <div className="rounded-2xl border border-[#D4FF00]/25 bg-gradient-to-br from-[#0E2B1E] to-[#071B12] p-8 md:p-12 relative overflow-hidden">
                        <div
                            className="absolute -top-20 -right-20 w-[400px] h-[400px] rounded-full pointer-events-none"
                            style={{
                                background:
                                    "radial-gradient(closest-side, rgba(212,255,0,0.12), transparent 70%)",
                            }}
                            aria-hidden
                        />
                        <div className="relative max-w-4xl">
                            <div className="text-[#D4FF00] font-display text-6xl md:text-7xl leading-none">
                                "
                            </div>
                            <blockquote className="font-display font-bold uppercase text-2xl md:text-3xl lg:text-4xl tracking-tight leading-tight -mt-3">
                                {c.quote}{" "}
                                <span className="text-[#D4FF00]">{c.quoteAccent}</span>{" "}
                                {c.quoteRest}
                            </blockquote>
                            <div className="mt-6 text-sm text-white/55 uppercase tracking-widest font-semibold">
                                {c.tagline}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
